import React, { useRef } from 'react'
import { Transition } from '@headlessui/react'
import classNames from 'classnames'
import { Maybe } from 'graphql/jsutils/Maybe'
import { When } from 'react-if'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { BoundedImage } from '@/molecules/BoundedImage'
import { LivestreamThumbnail } from '@/molecules/LivestreamThumbnail'
import { ShopifyStoreConfig } from '@/services/EarlyAccessService'
import { Project, ResumeWatchingData } from '@/services/ProjectsService'
import { useWatchProjectContext } from '@/services/ProjectsService/WatchProjectProvider'
import { ProjectTheme } from '@/types/codegen-contentful'
import { Media } from '@/utils/EpisodeUtil/Media'
import { HeroProjectDescription } from './HeroProjectDescription'
import { ResponsiveBackgroundContainer } from './ResponsiveBackgroundContainer'
import { WatchHeroCTAs } from './WatchHeroCTAs'
import { WatchProgressStatus } from './WatchProgressStatus'
import type { BackgroundPosition } from './types'

export type { BackgroundPosition } from './types'

export interface WatchProjectHeroProps {
  className?: string
  cloudinaryImgPath: string
  duration?: number
  position?: number
  slug: string
  showTitle?: Maybe<string>
  showDescription?: string
  theaterBackgroundPosition?: BackgroundPosition
  isGuildMemberContent?: boolean
  shopifyConfig?: ShopifyStoreConfig
  contentfulProjectTheme: Maybe<ProjectTheme>
  guildScore?: number
  isGuildMember: boolean
  isGeoBlockedProject?: boolean
  projectData: Project
  shareCtaPath?: string | null
  watchableReasons?:
    | never[]
    | [
        {
          __typename: string
          startDateTime: string
        },
      ]
  guildCodes?: string[]
  areGuildTicketsAvailable?: boolean
  activeSweepstake?: boolean
  showParentalControlsExperiment: boolean
  skipsEnabled: boolean
  movieSpecialMedias?: Media[]
  darkenBackground?: boolean
  onPrimaryCtaGuidChange?: (guid: string | null) => void
  resumeWatchingData?: ResumeWatchingData
}

export const WatchProjectHero: React.FC<WatchProjectHeroProps> = (props) => {
  const {
    className,
    cloudinaryImgPath,
    duration,
    position,
    slug,
    showTitle,
    showDescription,
    theaterBackgroundPosition,
    isGuildMemberContent,
    shopifyConfig,
    contentfulProjectTheme,
    guildScore,
    isGuildMember,
    isGeoBlockedProject,
    projectData,
    shareCtaPath,
    watchableReasons = [],
    guildCodes = [],
    areGuildTicketsAvailable = false,
    activeSweepstake = false,
    showParentalControlsExperiment = false,
    skipsEnabled = false,
    movieSpecialMedias = [],
    darkenBackground = false,
    onPrimaryCtaGuidChange,
    resumeWatchingData,
  } = props
  const contentRef = useRef<HTMLDivElement>(null)
  const { bonusVideos, currentLivestream } = useWatchProjectContext()

  const backgroundImageUrl = projectData?.title?.landscapeBackdrop?.cloudinaryPath ?? cloudinaryImgPath
  const backgroundTabletImageUrl = projectData?.title?.landscapeBackdrop?.cloudinaryPath ?? cloudinaryImgPath
  const backgroundMobileImageUrl = projectData?.title?.squareNonTitleImage?.cloudinaryPath ?? cloudinaryImgPath

  return (
    <>
      <div
        className={classNames([
          'flex flex-col gap-4 relative w-full lg:flex-row lg:flex-wrap md:min-h-[50vh] lg:min-h-[70vh] xl:min-h-[75vh] 2xl:min-h-[630px] bg-core-gray-950 overflow-y-hidden',
          className,
        ])}
        ref={contentRef}
      >
        <ResponsiveBackgroundContainer
          cloudinaryImgPath={backgroundImageUrl}
          cloudinaryTabletImgPath={backgroundTabletImageUrl}
          cloudinaryMobileImgPath={backgroundMobileImageUrl}
          theaterBackgroundPosition={theaterBackgroundPosition}
          contentRef={contentRef}
          darkenBackground={darkenBackground}
          projectView
        />

        <PaddedContainer fluid className="flex items-center justify-center">
          <div className="relative mt-[23vh] w-full pt-24 md:mt-10 md:max-w-[600px] xl:max-w-[650px]">
            <BoundedImage
              className="mb-2 flex items-center justify-center md:justify-start"
              alt={showTitle ?? showDescription}
              path={projectData?.title?.logo?.cloudinaryPath ?? projectData?.logoCloudinaryPath}
              width={600}
              height={104}
            />
            <HeroProjectDescription
              showTitle={showTitle}
              showDescription={showDescription}
              showEarlyAccess={isGuildMemberContent}
              slug={slug}
              contentfulProjectTheme={contentfulProjectTheme}
              guildScore={guildScore}
            />
            <WatchProgressStatus position={position} duration={duration} />
            <WatchHeroCTAs
              projectData={projectData}
              bonusVideos={bonusVideos}
              shopifyConfig={shopifyConfig}
              pifEnabled={projectData?.pifEnabled ?? false}
              shareCtaPath={shareCtaPath}
              isGuildMemberContent={isGuildMemberContent}
              isGuildMember={isGuildMember}
              watchableReasons={watchableReasons as string[]}
              guildCodes={guildCodes}
              areGuildTicketsAvailable={areGuildTicketsAvailable}
              activeSweepstake={activeSweepstake}
              isGeoBlockedProject={isGeoBlockedProject}
              showParentalControlsExperiment={showParentalControlsExperiment}
              skipsEnabled={skipsEnabled}
              movieSpecialMedias={movieSpecialMedias}
              onPrimaryCtaGuidChange={onPrimaryCtaGuidChange}
              resumeWatchingData={resumeWatchingData}
            />
          </div>
        </PaddedContainer>
        <div
          className="relative mx-auto mt-4 flex items-center sm:mt-0 md:mx-0 md:ml-auto md:items-end"
          id="current-livestream"
        >
          <When condition={Boolean(currentLivestream?.guid && currentLivestream?.bannerCloudinaryPath)}>
            <Transition
              show
              appear
              enter="transition-opacity duration-1000"
              enterFrom="opacity-0"
              enterTo="opacity-100"
              leave="transition-opacity duration-1000"
              leaveFrom="opacity-100"
              leaveTo="opacity-0"
            >
              <PaddedContainer fluid>
                {currentLivestream && currentLivestream.guid && currentLivestream.bannerCloudinaryPath && (
                  <LivestreamThumbnail
                    guid={currentLivestream.guid}
                    name={currentLivestream.name ?? undefined}
                    bannerCloudinaryPath={currentLivestream.bannerCloudinaryPath}
                    live={currentLivestream.live ?? false}
                    startAt={currentLivestream.startAt ?? ''}
                    isEnded={currentLivestream.isEnded ?? false}
                    unavailableReason={currentLivestream.unavailableReason ?? undefined}
                    className="h-[180px] w-[320px]"
                    animationClassName="ease-in-out duration-[1000ms] md:hover:scale-[1.02]"
                  />
                )}
              </PaddedContainer>
            </Transition>
          </When>
        </div>
      </div>
    </>
  )
}
